import { CustomerOrderSummary } from "@/components/CustomerOrderSummary";
import { customerItemTotal } from "../../../../lib/ordering-pricing";
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { useGetMyOrders, getGetMyOrdersQueryKey } from "@workspace/api-client-react";
import type { OrderWithItems } from "@workspace/api-client-react";
import { ShoppingBag, ChevronDown, ChevronUp, ArrowLeft, RefreshCw, Truck, Layers, ChevronRight, Clock, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { DeliveryCodeCard } from "@/components/hub";

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

/** Matches the mobile order screen and the server's CUSTOMER_CANCEL_MIN_AGE_MS. */
const CANCEL_UNLOCK_MS = 20 * 60 * 1000;
/** When to suggest ringing — the shop's push escalation is spent by ~4m25s. */
const CALL_PROMPT_MS = 6 * 60 * 1000;

/**
 * What a customer sees while a shop has not accepted their order.
 *
 * Web had nothing here at all: the only thing that ever resolved a stuck web
 * order was the server auto-refunding it at 15 minutes, which has been removed
 * because it cancelled deliverable orders and gave the customer no say. Without
 * this the web customer would wait indefinitely with no way out, so parity with
 * the mobile screen is not cosmetic — it is the whole recourse.
 */
function PendingOrderNotice({ order }: { order: OrderWithItems }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const createdMs = order.createdAt ? new Date(order.createdAt).getTime() : Date.now();
  const [now, setNow] = useState(() => Date.now());
  const [cancelling, setCancelling] = useState(false);

  const ageMs = now - createdMs;
  const unlocked = ageMs >= CANCEL_UNLOCK_MS;

  useEffect(() => {
    if (unlocked) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [unlocked]);

  const store = (order as unknown as { store?: { name?: string; phone?: string | null } | null }).store;
  const phone = store?.phone?.trim();
  const shopName = store?.name ?? "The shop";
  const suggestCall = ageMs >= CALL_PROMPT_MS;

  const remainMs = Math.max(0, CANCEL_UNLOCK_MS - ageMs);
  const mins = Math.floor(remainMs / 60000);
  const secs = String(Math.floor((remainMs % 60000) / 1000)).padStart(2, "0");

  async function handleCancel() {
    if (!window.confirm(`${shopName} hasn't accepted yet. Cancel this order and get a full refund?`)) return;
    setCancelling(true);
    try {
      const res = await fetch(`/api/order/orders/${order.id}/customer-cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ayrshire_token") ?? ""}` },
        credentials: "include",
      });
      if (res.ok) {
        const result = await res.json() as { refunded?: boolean; paymentMethod?: string };
        toast({ title: "Order cancelled", description: result.paymentMethod === "cash"
          ? "Your cash order has been cancelled."
          : result.refunded ? "Your refund has been processed." : "Your refund is being processed." });
        void qc.invalidateQueries({ queryKey: getGetMyOrdersQueryKey() });
      } else {
        const data = await res.json().catch(() => ({})) as { error?: string };
        toast({ title: "Could not cancel", description: data.error ?? "Please try again.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="mx-4 mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:bg-amber-900/20">
      <div className="flex items-start gap-2">
        <Clock size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
            {suggestCall ? "Still waiting on the shop" : "Waiting for the shop…"}
          </p>
          <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-200">
            {suggestCall
              ? `${shopName} hasn't accepted yet — they may be busy.${phone ? " A quick call is usually the fastest way to get it moving." : ""}`
              : `If they haven't accepted in ${Math.max(1, Math.ceil(remainMs / 60000))} min, you'll be able to cancel.`}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {suggestCall && phone && (
              <a
                href={`tel:${phone.replace(/\s+/g, "")}`}
                className="inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-amber-400 bg-white px-3 text-xs font-medium text-amber-800 hover:bg-amber-100"
              >
                <Phone size={13} /> Call {shopName}
              </a>
            )}
            {unlocked ? (
              <Button size="sm" variant="destructive" disabled={cancelling} onClick={() => void handleCancel()}>
                {cancelling ? "Cancelling…" : "Cancel & refund"}
              </Button>
            ) : (
              <span className="text-xs tabular-nums text-amber-700">{mins}:{secs} until you can cancel</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const STATUS: Record<string, { label: string; cls: string }> = {
  pending:          { label: "Pending",     cls: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 border-amber-200" },
  confirmed:        { label: "Confirmed",   cls: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 border-blue-200" },
  preparing:        { label: "Preparing",   cls: "bg-orange-50 text-orange-700 border-orange-200" },
  ready:            { label: "Ready",       cls: "bg-violet-50 text-violet-700 border-violet-200" },
  out_for_delivery: { label: "On the way",  cls: "bg-sky-50 text-sky-700 border-sky-200" },
  delivered:        { label: "Delivered",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled:        { label: "Cancelled",   cls: "bg-red-50 text-red-700 border-red-200" },
};

const COMPLETED_STATUSES = new Set(["delivered", "cancelled"]);

const STATUS_RANK: Record<string, number> = {
  pending: 0, confirmed: 1, preparing: 2, ready: 3,
  out_for_delivery: 4, delivered: 5, cancelled: -1,
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS[status] ?? { label: status, cls: "bg-slate-50 text-slate-600 border-slate-200" };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function MultiOrderGroupCard({ siblings }: { siblings: OrderWithItems[] }) {
  const [open, setOpen] = useState(false);

  type OrderEx = OrderWithItems & { isMultiOrderChild?: boolean; multiOrderId?: number | null; driverMode?: string | null; deliveryCode?: string | null; customerPlatformFeePence?: number | null; customerHubDriverFeePence?: number | null; customerUpliftMarginPence?: number | null; serviceChargePence?: number | null; bagChargePence?: number | null; multiOrderDeliveryFeePence?: number | null };

  const anchor = siblings[0]! as OrderEx;
  const isHubDelivery = anchor.driverMode === "hub" && anchor.fulfilmentType === "delivery";
  // Only the dispatch anchor of a multi-order carries a code, and it is not
  // necessarily siblings[0] — take the first sibling that actually has one,
  // otherwise the customer sees no code at all for a coded multi-order.
  const groupDeliveryCode = siblings.map((o) => (o as OrderEx).deliveryCode).find((c) => !!c) ?? null;

  // Combined status = most advanced
  const combinedStatus = siblings.reduce((best, o) => {
    const rank = STATUS_RANK[o.status] ?? 0;
    return rank > (STATUS_RANK[best] ?? 0) ? o.status : best;
  }, siblings[0]!.status);

  const isCompleted = COMPLETED_STATUSES.has(combinedStatus);
  const statusCfg = STATUS[combinedStatus] ?? { label: combinedStatus, cls: "bg-slate-50 text-slate-600 border-slate-200" };

  // Combined delivery total (informational — already included in each child's customerPaidPence)
  const deliveryFeePence = (anchor as OrderEx).multiOrderDeliveryFeePence ?? siblings.reduce((s, o) => s + (o.deliveryFeePence ?? 0), 0);

  // Combined total = sum of per-store customerPaidPence (each already includes delivery + fees)
  const combinedPaidPence = siblings.reduce((s, o) => s + ((o as OrderEx).customerPaidPence ?? o.totalPence ?? 0), 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      {/* Teal accent strip */}
      <div className="h-1 bg-[#1e3a5f] w-full" />

      {/* Header — clickable to expand */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-4 p-4 text-left hover:bg-slate-50/50 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-[#1e3a5f] flex items-center justify-center flex-shrink-0 mt-0.5">
          <Layers size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground truncate">
            {siblings.map((o) => o.store?.name ?? `Order #${o.id}`).join(" + ")}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {fmtDate(anchor.createdAt)} · {siblings.length} stores
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-[#1e3a5f] border border-slate-200">
              MULTI-STORE
            </span>
            <span className="font-bold text-foreground text-sm">{pence(combinedPaidPence)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <StatusBadge status={combinedStatus} />
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>

      {/* Per-store status rows */}
      <div className="px-4 pb-3 space-y-2">
        {siblings.map((o) => (
          <div key={o.id} className="flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
            {o.store?.imageUrl ? (
              <img src={o.store.imageUrl} alt={o.store.name ?? ""} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">{(o.store?.name ?? "?")[0]?.toUpperCase()}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{o.store?.name ?? `Order #${o.id}`}</p>
              <p className="text-xs text-slate-400">{(o.items ?? []).length} item{(o.items ?? []).length !== 1 ? "s" : ""}</p>
            </div>
            <StatusBadge status={o.status} />
          </div>
        ))}
      </div>

      {/* Action area */}
      {!isCompleted && (
        <div className="px-4 pb-3 space-y-2">
          {!!anchor.estimatedReadyAt && combinedStatus !== "out_for_delivery" && (
            <div className="flex items-center gap-2 bg-amber-50 rounded-xl px-3 py-2 border border-amber-200">
              <Clock size={16} className="text-amber-600 shrink-0" aria-hidden />
              <p className="text-xs font-semibold text-amber-800">
                Est. ready: <span className="font-bold">{new Date(anchor.estimatedReadyAt).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" })}</span>
              </p>
            </div>
          )}
          <Link
            href={`/order/track/${anchor.id}`}
            className="flex items-center justify-center gap-2 w-full py-2 rounded-xl border border-slate-300 text-[#1e3a5f] text-sm font-semibold hover:bg-slate-50 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <Truck size={13} />
            Track Progress
          </Link>
        </div>
      )}

      {/* Delivery code — visible for the whole life of an active hub delivery,
          not only out_for_delivery. Matches OrderTrack and mobile. */}
      {isHubDelivery && !!groupDeliveryCode && combinedStatus !== "delivered" && combinedStatus !== "cancelled" && (
        <DeliveryCodeCard
          code={groupDeliveryCode}
          enRoute={combinedStatus === "out_for_delivery"}
          className="mx-4 mb-3"
        />
      )}

      {/* Expanded details */}
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-4">
          {/* Per-store item lists */}
          {siblings.map((o, idx) => {
            const oEx = o as OrderEx;
            return (
              <div key={o.id} className={idx < siblings.length - 1 ? "pb-4 border-b border-slate-100" : ""}>
                <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-wide mb-2">{o.store?.name ?? `Order #${o.id}`}</p>
                <div className="space-y-1 mb-2">
                  {(o.items ?? []).map((item) => {
                    let optionLabel = "";
                    try {
                      const opts = item.selectedOptions ? (JSON.parse(item.selectedOptions) as Array<{ optionName?: string; groupName?: string }>) : [];
                      optionLabel = opts.map((op) => op.groupName ? `${op.groupName}: ${op.optionName ?? ""}` : (op.optionName ?? "")).filter(Boolean).join(", ");
                    } catch { /* */ }
                    return (
                      <div key={item.id}>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700">{item.quantity}× {item.name}</span>
                          <span className="text-slate-500">{pence(customerItemTotal(item))}</span>
                        </div>
                        {!!optionLabel && <p className="text-xs text-slate-400 ml-2">{optionLabel}</p>}
                        {!!item.notes && <p className="text-xs text-slate-400 italic ml-2">Note: {item.notes}</p>}
                      </div>
                    );
                  })}
                </div>
                <CustomerOrderSummary order={oEx} hideDelivery hideTotal />
              </div>
            );
          })}

          {/* Combined delivery + total */}
          <div className="space-y-1 border-t border-slate-100 pt-2">
            {deliveryFeePence > 0 && (
              <div className="flex justify-between text-xs text-slate-400">
                <span>Combined delivery</span>
                <span>{pence(deliveryFeePence)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-foreground pt-1 border-t border-slate-100">
              <span>Total paid</span>
              <span>{pence(combinedPaidPence)}</span>
            </div>
          </div>

          {anchor.deliveryAddress && (
            <p className="text-xs text-slate-400"> {anchor.deliveryAddress}</p>
          )}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order }: { order: OrderWithItems }) {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const items = order.items ?? [];
  const isCompleted = COMPLETED_STATUSES.has(order.status);
  const slug = order.store?.slug;

  function handleReorder(e: React.MouseEvent) {
    e.stopPropagation();
    if (!slug) {
      toast({ title: "Store no longer available", variant: "destructive", action: <ToastAction altText="Browse stores" onClick={() => navigate("/order")}>Browse stores</ToastAction> });
      return;
    }
    if (items.length === 0) {
      toast({ title: "No items to reorder", variant: "destructive" });
      return;
    }
    type CartItem = {
      cartKey: string;
      menuItemId: number;
      name: string;
      pricePence: number;
      quantity: number;
    };
    const cartItems: CartItem[] = items.map((item) => ({
      cartKey: `${item.menuItemId}_default`,
      menuItemId: item.menuItemId,
      name: item.name,
      pricePence: item.pricePence,
      quantity: item.quantity,
    }));
    try {
      localStorage.setItem(`order_cart_${slug}`, JSON.stringify(cartItems));
      localStorage.setItem(`order_cart_reorder_flag_${slug}`, "1");
    } catch {}
    navigate(`/order/${slug}`);
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-4 p-4 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <ShoppingBag size={18} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground truncate">
            {order.store?.name ?? `Order #${order.id}`}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {fmtDate(order.createdAt)} · {order.fulfilmentType === "delivery" ? "Delivery" : "Collection"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <StatusBadge status={order.status} />
          <p className="font-bold text-foreground text-sm">{pence(order.customerPaidPence ?? order.totalPence)}</p>
        </div>
        <div className="text-slate-400 mt-1">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      <div className="px-4 pb-3 -mt-1 flex items-stretch gap-2">
        <Link
          href={`/order/track/${order.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border border-primary/30 text-primary text-sm font-semibold hover:bg-primary/5 transition-colors"
        >
          <Truck size={13} />
          Track order
        </Link>
        {isCompleted && slug && (
          <button
            onClick={handleReorder}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            <RefreshCw size={13} />
            Reorder
          </button>
        )}
      </div>

      {/* Pending alternative offer banner */}
      {(order as unknown as { hasPendingAlternativeOffer?: boolean }).hasPendingAlternativeOffer && (
        <Link
          href={`/order/track/${order.id}`}
          onClick={(e) => e.stopPropagation()}
          className="mx-4 mb-2 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 hover:bg-amber-100 transition-colors"
        >
          <Clock size={18} className="text-amber-500 shrink-0" aria-hidden />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-900">Replacement item suggested</p>
            <p className="text-xs text-amber-700">The store can't fulfil one of your items — tap to accept or decline</p>
          </div>
          <ChevronRight size={16} className="text-amber-500 flex-shrink-0" />
        </Link>
      )}

      {/* Nothing cancels an unaccepted order automatically any more, so this is
          the customer's only route out of a shop that never responds. */}
      {order.status === "pending" && <PendingOrderNotice order={order} />}

      {/* Delivery code — visible for the whole life of an active hub delivery,
          not only out_for_delivery. Matches OrderTrack and mobile. */}
      {(order as unknown as { driverMode?: string }).driverMode === "hub" &&
       order.fulfilmentType === "delivery" &&
       order.status !== "delivered" && order.status !== "cancelled" &&
       !!(order as unknown as { deliveryCode?: string | null }).deliveryCode && (
        <DeliveryCodeCard
          code={(order as unknown as { deliveryCode: string }).deliveryCode}
          enRoute={order.status === "out_for_delivery"}
          className="mx-4 mb-3"
        />
      )}

      {open && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-3">
          {items.length > 0 && (
            <div className="space-y-1.5">
              {items.map((item) => {
                const fullyRefunded = ((item as unknown as { refundedQuantity?: number }).refundedQuantity ?? 0) >= item.quantity;
                let optionLabel = "";
                try {
                  const opts = item.selectedOptions ? (JSON.parse(item.selectedOptions) as Array<{ optionName?: string }>) : [];
                  optionLabel = opts.map((op) => op.optionName ?? "").filter(Boolean).join(", ");
                } catch { /* */ }
                return (
                  <div key={item.id} className={fullyRefunded ? "opacity-50" : ""}>
                    <div className="flex justify-between text-sm items-center">
                      <span className={`text-slate-700 ${fullyRefunded ? "line-through" : ""}`}>{item.quantity}× {item.name}</span>
                      {fullyRefunded
                        ? <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">Removed</span>
                        : <span className="text-slate-500">{pence(customerItemTotal(item))}</span>
                      }
                    </div>
                    {!!optionLabel && <p className="text-xs text-slate-400 ml-2">{optionLabel}</p>}
                    {!!item.notes && <p className="text-xs text-slate-400 italic ml-2">Note: {item.notes}</p>}
                  </div>
                );
              })}
            </div>
          )}
          <CustomerOrderSummary order={order} />
          {order.deliveryAddress && (
            <p className="text-xs text-slate-400"> {order.deliveryAddress}</p>
          )}
          {order.notes && (
            <p className="text-xs text-slate-400"> {order.notes}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function MyOrders() {
  const { user } = useAuth();
  const activeStatuses = new Set(["pending", "confirmed", "preparing", "ready", "out_for_delivery"]);

  const { data, isLoading, refetch } = useGetMyOrders({
    query: {
      queryKey: getGetMyOrdersQueryKey(),
      staleTime: 15000,
      enabled: !!user,
      refetchInterval: (query) => {
        const items = (query.state.data as { items?: Array<{ status: string }> } | undefined)?.items ?? [];
        return items.some((o) => activeStatuses.has(o.status)) ? 30000 : false;
      },
    },
  });

  const orders = data?.items ?? [];

  type OrderEx = OrderWithItems & { isMultiOrderChild?: boolean; multiOrderId?: number | null };
  type GroupItem = { kind: "single"; order: OrderWithItems } | { kind: "multi"; siblings: OrderWithItems[] };

  const groupedItems: GroupItem[] = (() => {
    const groups: GroupItem[] = [];
    const seen = new Set<number>();
    for (const order of orders as OrderEx[]) {
      if (seen.has(order.id)) continue;
      if (order.isMultiOrderChild && order.multiOrderId) {
        const siblingIds = new Set<number>();
        const sibs = (orders as OrderEx[]).filter((o) => o.multiOrderId === order.multiOrderId);
        if (sibs.length > 1) {
          sibs.forEach((s) => { seen.add(s.id); siblingIds.add(s.id); });
          groups.push({ kind: "multi", siblings: sibs });
          continue;
        }
      }
      seen.add(order.id);
      groups.push({ kind: "single", order });
    }
    return groups;
  })();

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-sm w-full text-center space-y-4">
          <ShoppingBag size={36} className="text-primary mx-auto" />
          <h2 className="text-xl font-bold text-foreground">Sign in to view orders</h2>
          <p className="text-slate-500 text-sm">Log in to see your order history from local shops.</p>
          <Link href="/login">
            <Button className="w-full bg-primary hover:bg-primary/90">Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-5 py-8 pb-16 space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-primary transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-[34px] leading-tight tracking-[-1.2px] font-bold text-[#1e3a5f]">My Orders</h1>
            <p className="text-sm text-slate-400">Your order history from local shops</p>
          </div>
          <button
            onClick={() => void refetch()}
            className="ml-auto text-xs text-primary hover:underline"
          >
            Refresh
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
              <ShoppingBag size={28} className="text-slate-400" />
            </div>
            <div>
              <p className="font-semibold text-slate-700 text-lg">No orders yet</p>
              <p className="text-slate-400 text-sm mt-1">Browse local shops and restaurants to place your first order.</p>
            </div>
            <Link href="/order">
              <Button className="bg-primary hover:bg-primary/90">Browse stores</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {groupedItems.map((group, idx) =>
              group.kind === "multi"
                ? <MultiOrderGroupCard key={`multi-${group.siblings[0]!.id}`} siblings={group.siblings} />
                : <OrderCard key={group.order.id} order={group.order} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
